/**
 * StyledComponents.ts
 * 
 * This file contains reusable styled components based on the design system.
 * These components provide consistent styling across the application.
 */

import styled from 'styled-components';
import { 
  ThemeColors, 
  Spacing, 
  Typography, 
  BorderRadius, 
  Shadows,
  ComponentStyles 
} from './ThemeStyles';

// Layout Components

export const PageContainer = styled.div<{ isMenuCollapsed?: boolean }>`
  display: flex;
  min-height: 100vh;
  background-color: ${ThemeColors.background.default};
  margin-left: ${props => props.isMenuCollapsed ? ComponentStyles.sidebar.collapsedWidth : ComponentStyles.sidebar.width};
  margin-top: ${ComponentStyles.header.height};
  transition: margin-left 0.3s ease;
  
  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

export const MainContent = styled.main`
  flex: 1;
  padding: ${Spacing.md};
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
`;

export const ContentSection = styled.section`
  margin-bottom: ${Spacing.md};
`;

export const SectionTitle = styled.h2`
  color: ${ThemeColors.text.primary};
  font-family: ${Typography.fontFamily.secondary};
  font-size: ${Typography.fontSize.xl};
  font-weight: ${Typography.fontWeight.semiBold};
  margin-bottom: ${Spacing.md};
`;

export const SectionSubtitle = styled.h3`
  color: ${ThemeColors.text.secondary};
  font-family: ${Typography.fontFamily.secondary};
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.medium};
  margin-bottom: ${Spacing.md};
`;

export const FlexRow = styled.div<{ gap?: string }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: ${props => props.gap || Spacing.md};
`;

export const FlexColumn = styled.div<{ gap?: string }>`
  display: flex;
  flex-direction: column;
  gap: ${props => props.gap || Spacing.md};
`;

export const Grid = styled.div<{ minWidth?: string; gap?: string }>`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(${props => props.minWidth || '300px'}, 1fr));
  gap: ${props => props.gap || Spacing.lg};
  width: 100%;
`;

// Card Components

export const Card = styled.div`
  background-color: ${ThemeColors.background.cards};
  border-radius: ${BorderRadius.lg};
  padding: ${Spacing.lg};
  box-shadow: ${Shadows.card};
  transition: box-shadow 0.3s ease;
  
  &:hover {
    box-shadow: ${Shadows.lg};
  }
`;

export const CardHeader = styled.div`
  padding: ${Spacing.md} ${Spacing.lg};
  margin: -${Spacing.lg} -${Spacing.lg} 0;
  border-bottom: 1px solid ${ThemeColors.border.light};
  border-top-left-radius: ${BorderRadius.lg};
  border-top-right-radius: ${BorderRadius.lg};
`;

export const CardTitle = styled.h3`
  color: ${ThemeColors.text.primary};
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.semiBold};
  margin: 0;
`;

export const CardContent = styled.div`
  padding: ${Spacing.lg};
  margin: 0 -${Spacing.lg};
`;

export const CardFooter = styled.div`
  padding: ${Spacing.md} ${Spacing.lg};
  margin: 0 -${Spacing.lg} -${Spacing.lg};
  border-top: 1px solid ${ThemeColors.border.light};
  border-bottom-left-radius: ${BorderRadius.lg};
  border-bottom-right-radius: ${BorderRadius.lg};
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${Spacing.md};
`;

// Button Components - Consolidated base styles
export const BaseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${Spacing.sm} ${Spacing.md};
  border-radius: ${BorderRadius.md};
  font-family: ${Typography.fontFamily.primary};
  font-weight: ${Typography.fontWeight.medium};
  font-size: ${Typography.fontSize.base};
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  gap: ${Spacing.sm};
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(160, 201, 63, 0.3);
  }
`;

// Primary button (default)
export const Button = styled(BaseButton)`
  background-color: ${ThemeColors.primary.main};
  color: ${ThemeColors.primary.contrastText};
  
  &:hover {
    background-color: ${ThemeColors.primary.dark};
  }
  
  &:active {
    background-color: ${ThemeColors.primary.dark};
  }
  
  &:disabled {
    background-color: ${ThemeColors.gray[300]};
    color: ${ThemeColors.gray[500]};
  }
`;

export const PrimaryButton = Button;
export const SecondaryButton = styled(BaseButton)`
  background-color: ${ThemeColors.secondary.main};
  color: ${ThemeColors.secondary.contrastText};
  
  &:hover {
    background-color: ${ThemeColors.secondary.dark};
  }
  
  &:disabled {
    background-color: ${ThemeColors.gray[300]};
    color: ${ThemeColors.gray[500]};
  }
`;

export const OutlineButton = styled(BaseButton)`
  background-color: transparent;
  color: ${ThemeColors.primary.main};
  border: 1px solid ${ThemeColors.primary.main};
  
  &:hover {
    background-color: rgba(160, 201, 63, 0.08);
  }
  
  &:disabled {
    border-color: ${ThemeColors.gray[300]};
    color: ${ThemeColors.gray[500]};
  }
`;

export const TextButton = styled(BaseButton)`
  background-color: transparent;
  color: ${ThemeColors.primary.main};
  padding: ${Spacing.sm} ${Spacing.md};
  
  &:hover {
    background-color: rgba(160, 201, 63, 0.08);
  }
  
  &:disabled {
    color: ${ThemeColors.gray[500]};
  }
`;

export const IconButton = styled(BaseButton)`
  background-color: transparent;
  color: ${ThemeColors.text.primary};
  padding: ${Spacing.sm};
  border-radius: ${BorderRadius.round};
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
`;

// Form Components

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${Spacing.md};
`;

export const FormLabel = styled.label`
  color: ${ThemeColors.text.primary};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  margin-bottom: ${Spacing.xs};
`;

// Base input styles - consolidated
const BaseInput = styled.input`
  padding: ${Spacing.sm} ${Spacing.md};
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.md};
  font-family: ${Typography.fontFamily.primary};
  font-size: ${Typography.fontSize.base};
  color: ${ThemeColors.text.primary};
  background-color: ${ThemeColors.background.paper};
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 3px rgba(160, 201, 63, 0.2);
  }
  
  &::placeholder {
    color: ${ThemeColors.gray[500]};
  }
  
  &:disabled {
    background-color: ${ThemeColors.gray[100]};
    color: ${ThemeColors.gray[500]};
    cursor: not-allowed;
  }
  
  &.error {
    border-color: ${ThemeColors.error.main};
  }
`;

export const Input = BaseInput;

export const TextArea = styled(BaseInput).attrs({ as: 'textarea' })`
  min-height: 100px;
  resize: vertical;
`;

export const Select = styled(BaseInput).attrs({ as: 'select' })`
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23555555' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 40px;
`;

export const Checkbox = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  cursor: pointer;
  
  input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
  }
  
  .checkbox-custom {
    width: 20px;
    height: 20px;
    background-color: ${ThemeColors.background.paper};
    border: 1px solid ${ThemeColors.border.light};
    border-radius: ${BorderRadius.sm};
    position: relative;
    transition: all 0.2s ease;
  }
  
  input:checked ~ .checkbox-custom {
    background-color: ${ThemeColors.primary.main};
    border-color: ${ThemeColors.primary.main};
  }
  
  .checkbox-custom:after {
    content: "";
    position: absolute;
    display: none;
    left: 6px;
    top: 2px;
    width: 5px;
    height: 10px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
  
  input:checked ~ .checkbox-custom:after {
    display: block;
  }
  
  input:disabled ~ .checkbox-custom {
    background-color: ${ThemeColors.gray[100]};
    border-color: ${ThemeColors.border.light};
    cursor: not-allowed;
  }
  
  input:disabled ~ .checkbox-label {
    color: ${ThemeColors.gray[500]};
    cursor: not-allowed;
  }
`;

export const CheckboxLabel = styled.span`
  font-size: ${Typography.fontSize.base};
  color: ${ThemeColors.text.primary};
  user-select: none;
`;

// Typography Components

// Title alias for backward compatibility
export const Title = styled.h1<{ size?: 'h1' | 'h2' | 'h3' | 'h4' }>`
  font-family: ${Typography.fontFamily.secondary};
  font-size: ${props => 
    props.size === 'h1' ? Typography.fontSize.xxxl :
    props.size === 'h2' ? Typography.fontSize.xxl :
    props.size === 'h3' ? Typography.fontSize.xl :
    props.size === 'h4' ? Typography.fontSize.lg :
    Typography.fontSize.xl
  };
  font-weight: ${props => 
    (props.size === 'h1' || props.size === 'h2') ? Typography.fontWeight.bold : Typography.fontWeight.semiBold
  };
  color: ${ThemeColors.text.primary};
  margin-bottom: ${props => 
    (props.size === 'h1' || props.size === 'h2') ? Spacing.md : Spacing.sm
  };
  line-height: ${Typography.lineHeight.tight};
`;

export const Heading1 = styled.h1`
  font-family: ${Typography.fontFamily.secondary};
  font-size: ${Typography.fontSize.xxxl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.md};
  line-height: ${Typography.lineHeight.tight};
`;

export const Heading2 = styled.h2`
  font-family: ${Typography.fontFamily.secondary};
  font-size: ${Typography.fontSize.xxl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.md};
  line-height: ${Typography.lineHeight.tight};
`;

export const Heading3 = styled.h3`
  font-family: ${Typography.fontFamily.secondary};
  font-size: ${Typography.fontSize.xl};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.sm};
  line-height: ${Typography.lineHeight.tight};
`;

export const Heading4 = styled.h4`
  font-family: ${Typography.fontFamily.secondary};
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.sm};
  line-height: ${Typography.lineHeight.tight};
`;

export const Paragraph = styled.p`
  font-family: ${Typography.fontFamily.primary};
  font-size: ${Typography.fontSize.base};
  font-weight: ${Typography.fontWeight.regular};
  color: ${ThemeColors.text.primary};
  line-height: ${Typography.lineHeight.relaxed};
  margin-bottom: ${Spacing.md};
`;

export const Text = styled.span<{ size?: string; weight?: number; color?: string; lineHeight?: number | string }>`
  font-family: ${Typography.fontFamily.primary};
  font-size: ${props => props.size || Typography.fontSize.base};
  font-weight: ${props => props.weight || Typography.fontWeight.regular};
  color: ${props => props.color || ThemeColors.text.primary};
  line-height: ${props => props.lineHeight || Typography.lineHeight.normal};
`;

// Table Components

export const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.md};
  overflow: hidden;
`;

export const TableHead = styled.thead`
  background-color: ${ThemeColors.gray[50]};
`;

export const TableRow = styled.tr`
  border-bottom: 1px solid ${ThemeColors.border.light};
  
  &:hover {
    background-color: rgba(160, 201, 63, 0.04);
  }
  
  &.selected {
    background-color: rgba(160, 201, 63, 0.08);
  }
`;

export const TableHeader = styled.th`
  padding: ${Spacing.md};
  text-align: left;
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.secondary};
  border-bottom: 1px solid ${ThemeColors.border.light};
`;

export const TableCell = styled.td`
  padding: ${Spacing.md};
  border-bottom: 1px solid ${ThemeColors.border.light};
`;

export const TableBody = styled.tbody`
  & tr:last-child td {
    border-bottom: none;
  }
`;

// Badge Components

export const Badge = styled.span<{ background?: string; color?: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  border-radius: 16px;
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  background-color: ${props => props.background || ThemeColors.primary.light};
  color: ${props => props.color || ThemeColors.primary.main};
`;

export const StatusBadge = styled(Badge)<{ status?: 'success' | 'warning' | 'error' | 'info' | string }>`
  background-color: ${props => 
    props.status === 'success' ? ThemeColors.success.light :
    props.status === 'warning' ? ThemeColors.warning.light :
    props.status === 'error' ? ThemeColors.error.light :
    props.status === 'info' ? ThemeColors.info.light :
    ThemeColors.gray[200]
  };
  color: ${props => 
    props.status === 'success' ? ThemeColors.success.main :
    props.status === 'warning' ? ThemeColors.warning.main :
    props.status === 'error' ? ThemeColors.error.main :
    props.status === 'info' ? ThemeColors.info.main :
    ThemeColors.gray[700]
  };
`;

// Utility Components

export const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${ThemeColors.divider};
  margin: ${Spacing.md} 0;
`;

export const Spacer = styled.div<{ width?: string; height?: string; flex?: string | number }>`
  width: ${props => props.width || 'auto'};
  height: ${props => props.height || Spacing.md};
  flex: ${props => props.flex || 'none'};
`;

export const Avatar = styled.div<{ size?: string; bgColor?: string; color?: string; fontSize?: string }>`
  width: ${props => props.size || '40px'};
  height: ${props => props.size || '40px'};
  border-radius: ${BorderRadius.round};
  background-color: ${props => props.bgColor || ThemeColors.primary.main};
  color: ${props => props.color || ThemeColors.primary.contrastText};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${Typography.fontWeight.semiBold};
  font-size: ${props => props.fontSize || Typography.fontSize.md};
`;
